/* eslint-disable */
import React, { FC, useState, useEffect, useContext } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { decode } from 'entities';
import { AudienceList } from 'Components/Lists';
import {
  IndividualListGQL,
  CreatorPrefGQL,
  CurrentUserPrefGQL,
  UserPrefsCreate,
  UserPrefsUpdate,
} from './gqlQuery';
import { FormattedDate } from 'Utils/formattedDate';
import { useDispatch } from 'react-redux';
import { toastMessage } from '../Toast/actions/toast.actions';

import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import withErrorBoundary from 'HOC/ErrorBoundary';

interface Props {
  inst_id: string;
  account_id: string;
  search?: string;
  listAccountId?: string;

  listId: string;
  Selector?: any;
  listName: string;

  tableColumns: string[];
  filters: string;
  sort: string;

  page: number;
  size: number;

  refreshList: boolean;
  setRefreshList: (val: any) => void;
  checkedItems?: any;
  setUserInfo?: any;
  onChecked?: (id: string) => void;
  onUnChecked?: (id: string) => void;
  onTotalRecordsChanged?: (n: string) => void;
  onTotalPagesChanged?: (n: number) => void;

  //This will be used to determine if a checkbox has been previously checked while navigating through other pages
  isChecked?: (id: string) => boolean;
}

const formatLastActiveDate = (date: string) => {
  /*
    Incoming format:  YYYY-MM-DD

    Outgoing format:  MM/DD/YYYY
  */
  try {
    const dateOptions = { month: '2-digit', day: '2-digit', year: 'numeric' }, //Date Formatting options
      formatted = FormattedDate(date);
    return formatted;
  } catch (error) {
    return '-';
  }
};

const ALL_COLUMNS = [
  'name',
  'major',
  'gender',
  'ethnicity',
  'relativeInterestLevel',
  'hasScheduledVisitAllTime',
  'hasAppliedAllTime',
  'visitorType',
  'enroll',
  'city',
  'state',
  'country',
  'lastActive',
  'dateOfBirth',
  'lastDateActiveWithYouvisit',
  'sat',
  'act',
  'gpa',
  'phone',
  'email',
  'currentSchool',
  'postal',
  'levelOfStudy',
  'dateRegistered',
];
const DEFAULT_COLUMNS = [
  'name',
  'major',
  'visitorType',
  'enroll',
  'state',
  'country',
  'dateRegistered',
];
const DEFAULT_SORTING_COLUMN = 'dateRegistered';
const DEFAULT_SORTING_DIRECTION = 'desc';

const mapColumnsToKeys = (
  column: string,
  direction?: string,
  noUSALocation?: any
) => {
  switch (column) {
    case 'name':
      return `fullName:${direction}`;
    case 'relativeInterestLevel':
      return `relativeInterestLevel:${direction}`;
    case 'visitorType':
      return `visitorType:${direction}`;
    case 'enroll':
      return `enrollYear:${direction},enrollTerm:${direction}`;
    case 'lastActive':
      return `lastDateActiveWithInstitution:${direction}`;
    case 'location':
      return noUSALocation
        ? `country:${direction}`
        : `city:${direction},country:${direction}`;
    case 'dateOfBirth':
      return `birthDate:${direction}`;
    case 'lastDateActiveWithYouvisit':
      return `lastDateActiveWithYouvisit:${direction}`;
    case 'sat':
      return `sat:${direction}`;
    case 'act':
      return `act:${direction}`;
    case 'gpa':
      return `gpa:${direction}`;
    case 'phone':
      return `phone:${direction}`;
    case 'email':
      return `email:${direction}`;
    case 'currentSchool':
      return `currentSchool:${direction}`;
    case 'postal':
      return `postal:${direction}`;
    case 'major':
      return `major:${direction}`;
    case 'gender':
      return `gender:${direction}`;
    case 'ethnicity':
      return `ethnicity:${direction}`;
    case 'hasScheduledVisitAllTime':
      return `hasScheduledVisitAllTime:${direction}`;
    case 'hasAppliedAllTime':
      return `hasAppliedAllTime:${direction}`;
    case 'levelOfStudy':
      return `levelOfStudy:${direction}`;
    case 'dateRegistered':
      return `dateRegistered:${direction}`;
    case 'city':
      return `city:${direction}`;
    case 'state':
      return `state:${direction}`;
    case 'country':
      return `country:${direction}`;
    default:
      return '';
  }
};

function getLocation(city: string, state: string, country: string) {
  if (country && country === 'USA') {
    if (city && state) return city + ', ' + state;
    if (city) return city;
    if (state) return state;
    return '-';
  } else if (country && country !== 'USA') {
    return country;
  }
  return '-';
}

const IndividualListContainer: FC<Props> = (props: Props) => {
  const [userTableColumns, setUserTableColumns] = useState(DEFAULT_COLUMNS);
  const [userSortingColumn, setUserSortingColumn] = useState(
    DEFAULT_SORTING_COLUMN
  );
  const [userSortingDirection, setUserSortingDirection] = useState(
    DEFAULT_SORTING_DIRECTION
  );

  let userInfo = [];
  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();

  const [creatorPrefObj, setCreatorPrefObj] = useState(null);
  const [, setTotalRecords] = useState(0);
  const [individualList, setIndividualList] = useState([]);
  const [pageCount, setPageCount] = useState(10);
  const [prefId, setPrefId] = useState();
  const Dispatch = useDispatch();

  const onCompleted = (data: any) => {
    const key = Object.keys(data)[0];
    const id = JSON.parse(data[key].data.id);
    const pref = JSON.parse(decode(data[key].data.value));

    //local state is set ONLY after the mutation is successful
    setUserTableColumns(pref.tableColumns);
    setUserSortingColumn(pref.sortingColumn);
    setUserSortingDirection(pref.sortingDirection);
    setPrefId(id);
  };

  const [createUserPref] = useMutation(UserPrefsCreate, {
    onCompleted: onCompleted,
  });
  const [updateUserPref] = useMutation(UserPrefsUpdate, {
    onCompleted: onCompleted,
  });

  const {
    loading: creatorPrefLoading,
    error: creatorPrefError,
    data: creatorPrefData,
  } = useQuery(CreatorPrefGQL, {
    variables: {
      inst_id: props.inst_id,
      account_id: props.account_id,
      target_account_id: props.listAccountId,
    },
    // skip: props.listAccountId === props.account_id,
    fetchPolicy: 'cache-and-network',
    onCompleted: userPrefAllData => {
      const userPrefObj =
        userPrefAllData &&
        userPrefAllData.userPrefs &&
        userPrefAllData.userPrefs
          .filter((pref: any) => pref.category === 'column_config')
          .filter((pref: any) => pref.name === props.listId)[0];
      setCreatorPrefObj(userPrefObj);
    },
  });

  const {
    loading: userPrefLoading,
    error: userPrefError,
    data: userPrefAllData,
  } = useQuery(CurrentUserPrefGQL, {
    variables: { inst_id: props.inst_id, account_id: props.account_id },
    fetchPolicy: 'cache-and-network',
    skip: !creatorPrefData,
    onCompleted: userPrefAllData => {
      const userPrefObj =
        userPrefAllData &&
        userPrefAllData.userPrefs &&
        userPrefAllData.userPrefs
          .filter((pref: any) => pref.category === 'column_config')
          .filter((pref: any) => pref.name === props.listId)[0];

      const tableColumns =
        (userPrefObj &&
          userPrefObj.value &&
          JSON.parse(userPrefObj.value).tableColumns) ||
        (creatorPrefObj &&
          creatorPrefObj.value &&
          JSON.parse(creatorPrefObj.value).tableColumns) ||
        DEFAULT_COLUMNS;

      const sortingColumn =
        (userPrefObj &&
          userPrefObj.value &&
          JSON.parse(userPrefObj.value).sortingColumn) ||
        (creatorPrefObj &&
          creatorPrefObj.value &&
          JSON.parse(creatorPrefObj.value).sortingColumn) ||
        DEFAULT_SORTING_COLUMN;

      const sortingDirection =
        (userPrefObj &&
          userPrefObj.value &&
          JSON.parse(userPrefObj.value).sortingDirection) ||
        (creatorPrefObj &&
          creatorPrefObj.value &&
          JSON.parse(creatorPrefObj.value).sortingDirection) ||
        DEFAULT_SORTING_DIRECTION;

      setUserTableColumns(tableColumns);
      setUserSortingColumn(sortingColumn);
      setUserSortingDirection(sortingDirection);

      setPrefId((userPrefObj && userPrefObj.id) || null);
    },
  });

  function setUserPreferences(
    columns: string[],
    sortingColumn: string,
    sortingDirection: string,
    prefId: string
  ) {
    let variables = {
      inst_id: props.inst_id,
      account_id: props.account_id,
      name: props.listId,
      category: 'column_config',
      value: JSON.stringify({
        tableColumns: columns,
        sortingColumn: sortingColumn,
        sortingDirection: sortingDirection,
      }),
    };

    if (!prefId) {
      createUserPref({ variables: variables });
    } else {
      variables['id'] = prefId;
      updateUserPref({ variables: variables });
    }
  }

  const { loading, error, refetch } = useQuery(IndividualListGQL, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache', //This does not generate an api requst, when using cache-and-network
    variables: {
      inst_id: props.inst_id,
      id: props.listId,
      account_id: props.account_id,
      sort: mapColumnsToKeys(
        userSortingColumn,
        userSortingDirection,
        props.Selector.filter.demographics.geography.usa.selection === 'None'
      ),
      page: props.page,
      size: props.size,
      search: props.search,
    },
    onCompleted: data => {
      props.setRefreshList(false);
      let list = data?.audience?.listAudienceMembers?.profiles
        ? data.audience.listAudienceMembers.profiles.map((entry: any) => {
            Object.keys(entry).forEach(key =>
              entry[key] == null || entry[key] === ''
                ? (entry[key] = '-')
                : entry[key]
            );

            entry.name = `${entry.fullName}`;
            entry.lastActive = formatLastActiveDate(entry.lastActive);
            entry.lastDateActiveWithYouvisit = formatLastActiveDate(
              entry.lastDateActiveWithYouvisit
            );
            entry.dateRegistered = formatLastActiveDate(entry.dateRegistered);
            entry.location = getLocation(
              entry.city,
              entry.state,
              entry.country
            );
            userInfo.push({
              userKey: entry.userKeyConsolidated,
              userId: entry.id,
            });

            return entry;
          })
        : [];
      data &&
        data.audience.listAudienceMembers &&
        props.onTotalRecordsChanged &&
        props.onTotalRecordsChanged(
          data.audience.listAudienceMembers.totalRecords
        );

      data &&
        data.audience.listAudienceMembers &&
        props.onTotalPagesChanged &&
        props.onTotalPagesChanged(data.audience.listAudienceMembers.totalPages);

      setIndividualList(list);
      setPageCount(data?.audience?.listAudienceMembers?.profiles?.length);

      const totalRecords =
        data &&
        data.audience.listAudienceMembers &&
        props.onTotalRecordsChanged &&
        props.onTotalRecordsChanged(
          data.audience.listAudienceMembers.totalRecords
        );

      setTotalRecords(Number(totalRecords));

      props.setUserInfo(userInfo);
      // Combined Analytics event tracking
      track('audience_list_viewed', {
        screen: View(location.pathname),
        list_name: props.listName,
        list_id: props.listId, //This is the number of lists created by a user
        list_type:
          data.audience.list.attributes === 'custom' ? 'Custom' : 'Standard', //This is an existing list.
        audience_count: data.audience.listAudienceMembers.totalRecords,
        column_count: props.tableColumns.length,
      });

      track('audiences_explore_page_viewed', {
        initiated_by: 'Click',
        audience_count: data.audience.listAudienceMembers.totalRecords,
        column_count: props.tableColumns.length,
      });
    },
  });

  useEffect(() => {
    refetch();
  }, [props.refreshList]);
  return (
    <AudienceList
      checkedItems={props.checkedItems}
      pageCount={pageCount < 10 ? pageCount : 10}
      onChecked={props.onChecked}
      onUnChecked={props.onUnChecked}
      loading={
        loading ||
        userPrefLoading ||
        !userTableColumns ||
        !userSortingDirection ||
        !userSortingColumn
      }
      sort={{
        activeColumn: {
          name: userSortingColumn,
          direction: userSortingDirection,
        },
        events: {
          onClickSortHandler: (column: string) => {
            let newSortingDirection;
            if (column === userSortingColumn) {
              newSortingDirection =
                userSortingDirection === 'desc' ? 'asc' : 'desc';
            } else {
              newSortingDirection = 'desc';
            }

            setUserPreferences(
              userTableColumns,
              column,
              newSortingDirection,
              prefId
            );
          },
        },
      }}
      tableColumns={userTableColumns}
      sortingColumn={userSortingColumn}
      sortingDirection={userSortingDirection}
      allColumns={ALL_COLUMNS}
      defaultColumns={DEFAULT_COLUMNS}
      audienceData={{ list: individualList }}
      isChecked={props.isChecked ? props.isChecked : null}
      changeCols={(cols: any, sortingColumn: any, sortingDirection: any) => {
        setUserPreferences(cols, sortingColumn, sortingDirection, prefId);

        Dispatch(
          toastMessage({
            message: 'Changes are applied',
            position: 'bottom',
          })
        );
      }}
    />
  );
};

export default withErrorBoundary(IndividualListContainer);
